/**
 * Upload a file to a presigned PUT URL with optional progress reporting.
 * Uses XMLHttpRequest for upload progress (fetch has no progress events).
 */
export async function putFileToPresignedUrl(params: {
  uploadUrl: string;
  file: File;
  contentType?: string;
  onProgress?: (percent: number) => void;
}): Promise<void> {
  const { uploadUrl, file, contentType, onProgress } = params;

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    if (contentType) {
      xhr.setRequestHeader('Content-Type', contentType);
    }

    xhr.upload.onprogress = event => {
      if (!onProgress || !event.lengthComputable || event.total <= 0) return;
      onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
        return;
      }
      reject(new Error(`MinIO upload failed: ${xhr.status} ${xhr.responseText || xhr.statusText}`.trim()));
    };

    xhr.onerror = () => {
      reject(new Error('MinIO upload failed: network error (check CORS on MinIO for browser PUT)'));
    };

    xhr.onabort = () => {
      reject(new Error('MinIO upload aborted'));
    };

    xhr.send(file);
  });
}

/** Minimal DigestStream shape (not in all TS DOM libs yet). */
type DigestStreamConstructor = new (algorithm: string) => {
  readonly writable: WritableStream;
  readonly digest: Promise<ArrayBuffer>;
};

/** SHA-256 of a File without loading the entire blob into memory when DigestStream is available. */
export async function sha256FileHex(file: File): Promise<string> {
  const DigestStreamCtor = (
    globalThis as typeof globalThis & { DigestStream?: DigestStreamConstructor }
  ).DigestStream;
  if (DigestStreamCtor) {
    const digestStream = new DigestStreamCtor('SHA-256');
    await file.stream().pipeTo(digestStream.writable);
    const digest = await digestStream.digest;
    const bytes = new Uint8Array(digest);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }
  const buf = await file.arrayBuffer();
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}
